/**
 * mega-menu.js
 * Mega menú Shopify — lógica de interacción
 * Desktop: hover con delay, focus trap, cierre con Escape/clic externo
 * Mobile:  drawer fullscreen, overlay, scroll lock
 * Shared:  sticky scroll shadow, header transparente sobre hero
 */

(function () {
  'use strict';

  /* ── Selectores ────────────────────────────────────────────────────────── */
  const SELECTORS = {
    header:         '#mm-header',
    navTrigger:     '[data-nav-trigger]',
    navItem:        '[data-nav-item]',
    panel:          '[data-panel]',
    drawerTrigger:  '#mm-drawer-trigger',
    drawerClose:    '[data-drawer-close]',
    drawer:         '#mm-drawer',
    overlay:        '#mm-overlay',
    searchTrigger:  '#mm-search-trigger',
    searchPanel:    '#mm-search-panel',
  };

  /* ── Tiempos ───────────────────────────────────────────────────────────── */
  const HOVER_OPEN_DELAY  = 120;   // ms antes de abrir el panel
  const HOVER_CLOSE_DELAY = 200;   // ms antes de cerrar (evita parpadeo al mover ratón)

  /* ── Estado ────────────────────────────────────────────────────────────── */
  let openPanelId     = null;
  let openTimer       = null;
  let closeTimer      = null;
  let drawerOpen      = false;
  let searchOpen      = false;
  let focusableBefore = null;   // elemento enfocado antes de abrir el drawer

  /* ── Referencias DOM ───────────────────────────────────────────────────── */
  const header        = document.querySelector(SELECTORS.header);
  const drawer        = document.querySelector(SELECTORS.drawer);
  const overlay       = document.querySelector(SELECTORS.overlay);
  const drawerTrigger = document.querySelector(SELECTORS.drawerTrigger);
  const searchPanel   = document.querySelector(SELECTORS.searchPanel);
  const searchTrigger = document.querySelector(SELECTORS.searchTrigger);

  if (!header) return;   // Guard: si no existe el header, salimos

  /* ════════════════════════════════════════════════════════════════════════
     DESKTOP — Hover manager
     ════════════════════════════════════════════════════════════════════════ */

  function getPanel(triggerId) {
    const panelId = triggerId.replace('mm-nav-trigger-', 'mm-panel-');
    return document.getElementById(panelId);
  }

  function getTrigger(panelId) {
    const triggerId = panelId.replace('mm-panel-', 'mm-nav-trigger-');
    return document.getElementById(triggerId);
  }

  function openPanel(trigger) {
    const panel = getPanel(trigger.id);
    if (!panel || openPanelId === panel.id) return;

    if (openPanelId) {
      const prevPanel = document.getElementById(openPanelId);
      _closePanel(prevPanel, true);
    }

    openPanelId = panel.id;
    panel.removeAttribute('hidden');

    // Actualizar top del panel fixed según la posición actual del header
    const headerRect = header.getBoundingClientRect();
    panel.style.top = headerRect.bottom + 'px';

    // Posicionar el panel horizontalmente bajo el trigger, sin salirse de la pantalla
    const triggerRect = trigger.getBoundingClientRect();
    const panelWidth  = panel.offsetWidth || panel.getBoundingClientRect().width || 320;
    let leftPos = triggerRect.left;

    // Si el panel se saldría por la derecha, lo pegamos al borde derecho de la ventana
    const viewportWidth = window.innerWidth;
    if (leftPos + panelWidth > viewportWidth) {
      leftPos = viewportWidth - panelWidth - 16; // 16px de margen
    }
    // Nunca menor que 0
    if (leftPos < 0) leftPos = 0;

    panel.style.left = leftPos + 'px';

    panel.offsetHeight;
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    overlay.classList.add('is-visible');
  }

  function closePanel(immediate = false) {
    if (!openPanelId) return;
    const panel = document.getElementById(openPanelId);
    if (!panel) return;
    _closePanel(panel, immediate);
  }

  function _closePanel(panel, immediate = false) {
    if (!panel) return;
    const trigger = getTrigger(panel.id);

    panel.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    const duration = immediate ? 0 : 320;

    setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.setAttribute('hidden', '');
      }
    }, duration);

    if (openPanelId === panel.id) {
      openPanelId = null;
      overlay.classList.remove('is-visible');
    }
  }

  function clearTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
  }

  // ── Eventos del nav (hover + click + teclado) ──────────────────────────
  const nav = document.querySelector('#mm-nav');

  if (nav) {

    // HOVER: abrir con delay
    nav.addEventListener('mouseover', (e) => {
      const trigger = e.target.closest(SELECTORS.navTrigger);
      if (!trigger) return;
      clearTimers();
      openTimer = setTimeout(() => openPanel(trigger), HOVER_OPEN_DELAY);
    });

    // CLICK: toggle del panel (para touch / usuarios que prefieren click)
    nav.addEventListener('click', (e) => {
      const trigger = e.target.closest(SELECTORS.navTrigger);
      if (!trigger) return;
      e.preventDefault();
      const panel = getPanel(trigger.id);
      if (panel && panel.classList.contains('is-open')) {
        closePanel(true);
      } else {
        clearTimers();
        openPanel(trigger);
      }
    });

    // MOUSELEAVE del nav: iniciar cierre con delay
    nav.addEventListener('mouseleave', () => {
      clearTimers();
      closeTimer = setTimeout(() => closePanel(), HOVER_CLOSE_DELAY);
    });

    // Teclado
    nav.addEventListener('keydown', (e) => {
      const trigger = e.target.closest(SELECTORS.navTrigger);
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        const panel = getPanel(trigger.id);
        if (panel && panel.classList.contains('is-open')) {
          closePanel(true);
        } else {
          openPanel(trigger);
        }
      }
      if (e.key === 'Escape' && openPanelId) {
        closePanel(true);
        const activeTrigger = getTrigger(openPanelId);
        if (activeTrigger) activeTrigger.focus();
      }
    });
  }

  // ── Eventos del panel (fixed, fuera del nav) ─────────────────────────
  // Como el panel es position:fixed y está fuera del nav,
  // necesitamos listeners directos en él usando event delegation en document
  document.addEventListener('mouseover', (e) => {
    if (!openPanelId) return;
    const panel = e.target.closest(SELECTORS.panel);
    if (panel) clearTimers(); // el ratón está sobre el panel → no cerrar
  });

  document.addEventListener('mouseleave', (e) => {
    // Solo si el ratón sale completamente de la ventana
    if (e.relatedTarget === null) closePanel();
  });

  // Cerrar al hacer clic fuera
  document.addEventListener('click', (e) => {
    if (!openPanelId) return;
    const inNav   = e.target.closest('#mm-nav');
    const inPanel = e.target.closest(SELECTORS.panel);
    if (!inNav && !inPanel) closePanel(true);
  });

  // Escape global
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openPanelId) closePanel(true);
  });


  /* ════════════════════════════════════════════════════════════════════════
     MOBILE — Drawer
     ════════════════════════════════════════════════════════════════════════ */

  function openDrawer() {
    if (drawerOpen) return;
    drawerOpen = true;
    focusableBefore = document.activeElement;

    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    drawerTrigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Enfocar el primer elemento del drawer
    requestAnimationFrame(() => {
      const firstFocusable = drawer.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    });

    // Trap de foco dentro del drawer
    drawer.addEventListener('keydown', trapFocus);
    document.addEventListener('keydown', handleDrawerEscape);
  }

  function closeDrawer() {
    if (!drawerOpen) return;
    drawerOpen = false;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    drawerTrigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // Devolver foco al trigger
    if (focusableBefore) focusableBefore.focus();

    drawer.removeEventListener('keydown', trapFocus);
    document.removeEventListener('keydown', handleDrawerEscape);
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.closest('[hidden]') && getComputedStyle(el).display !== 'none');

    if (!focusables.length) return;
    const first = focusables[0];
    const last  = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function handleDrawerEscape(e) {
    if (e.key === 'Escape') closeDrawer();
  }

  if (drawerTrigger) drawerTrigger.addEventListener('click', openDrawer);

  document.querySelectorAll(SELECTORS.drawerClose).forEach(btn => {
    btn.addEventListener('click', closeDrawer);
  });

  if (overlay) {
    overlay.addEventListener('click', () => {
      closeDrawer();
      closePanel(true);
      if (searchOpen) closeSearch();
    });
  }


  /* ════════════════════════════════════════════════════════════════════════
     BÚSQUEDA — toggle del panel
     ════════════════════════════════════════════════════════════════════════ */

  function openSearch() {
    if (!searchPanel || searchOpen) return;
    searchOpen = true;
    searchPanel.removeAttribute('hidden');
    searchTrigger.setAttribute('aria-expanded', 'true');
    overlay.classList.add('is-visible');

    const input = searchPanel.querySelector('[data-search-input]');
    if (input) requestAnimationFrame(() => input.focus());
  }

  function closeSearch() {
    if (!searchPanel || !searchOpen) return;
    searchOpen = false;
    searchPanel.setAttribute('hidden', '');
    searchTrigger.setAttribute('aria-expanded', 'false');
    overlay.classList.remove('is-visible');
  }

  if (searchTrigger) {
    searchTrigger.addEventListener('click', () => {
      searchOpen ? closeSearch() : openSearch();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && searchOpen) closeSearch();
  });


  /* ════════════════════════════════════════════════════════════════════════
     STICKY — Sombra al hacer scroll
     ════════════════════════════════════════════════════════════════════════ */

  const transparent = header.dataset.transparent === 'true';

  function onScroll() {
    const scrolled = window.scrollY > 10;
    header.classList.toggle('is-scrolled', scrolled);

    if (transparent) {
      header.classList.toggle('mm-header--transparent', !scrolled);
    }
  }

  // Aplicar estado inicial
  onScroll();

  // IntersectionObserver es más eficiente que scroll listener para este caso
  // ── Agregar mouseleave a cada panel para cerrar al salir ──────────────
  document.querySelectorAll(SELECTORS.panel).forEach(panel => {
    panel.addEventListener('mouseenter', () => clearTimers());
    panel.addEventListener('mouseleave', () => {
      clearTimers();
      closeTimer = setTimeout(() => closePanel(), HOVER_CLOSE_DELAY);
    });
  });

  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:80px;left:0;width:1px;height:1px;pointer-events:none;';
  document.body.prepend(sentinel);

  const scrollObserver = new IntersectionObserver(
    ([entry]) => {
      const scrolled = !entry.isIntersecting;
      header.classList.toggle('is-scrolled', scrolled);
      if (transparent) header.classList.toggle('mm-header--transparent', !scrolled);
    },
    { threshold: 0 }
  );
  scrollObserver.observe(sentinel);


  /* ════════════════════════════════════════════════════════════════════════
     ACCORDION MOBILE — animación CSS grid rows
     (el toggle lo hace <details> nativo; aquí solo manejamos
     el cierre de otros acordeones cuando se abre uno nuevo)
     ════════════════════════════════════════════════════════════════════════ */

  const accordions = document.querySelectorAll('[data-accordion]');

  accordions.forEach(accordion => {
    accordion.addEventListener('toggle', () => {
      if (accordion.open) {
        // Cerrar los demás
        accordions.forEach(other => {
          if (other !== accordion && other.open) other.open = false;
        });
      }
    });
  });

})();